import React, { FC, memo, ReactNode } from 'react'
import s from './Favorite.module.sass'
import { middlewareAuthorized } from '@shared/lib/helpers/auth'
import { useAppDispatch, useAppSelector } from '@app/model/store'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { EGG } from '@shared/api/analytics'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import { PickAnalytic } from '@shared/api/analytics/types/types'
import { addFavoriteObject, deleteFavoriteObject } from '@app/model/slice'
import { FavoritePropsToStore, TypeCategoryFavoriteObject } from '@app/model/types'
import { TypeMappingSelectionOutput } from '@shared/api/middleware/mappingAPI/selection/type'
import { TypeMappingNewsOutput } from '@shared/api/middleware/mappingAPI/news/type'
import { addTemporaryNotice } from '@widgets/Common/Notices'
import { MAPPING_API_GQL } from '@shared/api/middleware/mappingAPI'
import { GQL_TAGS } from '@shared/api/api.gql'
import { LightGQL } from '@shared/api/gql-service/LightGQLService'

interface Props {
  category: TypeCategoryFavoriteObject
  id: number
  children: ReactNode
  analytic: {
    data: TypeMappingRestOutput | TypeMappingSelectionOutput | TypeMappingNewsOutput
    other: PickAnalytic<'section_name'>
  }
  className?: string
}

enum CategoryForRequest {
  'restaurant' = 'Restaurant',
  'selection' = 'Selection',
  'concert' = 'Concert',
  'news' = 'Article',
}

const FavoriteContainer: FC<Props> = ({ children, id, category, ...props }) => {
  const active = useAppSelector((state) => {
    const groupName = FavoritePropsToStore[category]
    const favoritesGroup = state.global.user.data?.favorites_objects[groupName]
    return favoritesGroup ? id in favoritesGroup : false
  })

  if (category === 'selection' && !process.env.IS_SHOW_FAVORITE_BUTTON_SELECTION) return
  if (category === 'news' && !process.env.SHOW_FAVORITE_BUTTON_NEWS) return

  return (
    <Favorite active={active} id={id} category={category} {...props}>
      {children}
    </Favorite>
  )
}

interface PropsFavorite {
  active: boolean
  category: TypeCategoryFavoriteObject
  id: number
  children: ReactNode
  analytic: {
    data: TypeMappingRestOutput | TypeMappingSelectionOutput | TypeMappingNewsOutput
    other: PickAnalytic<'section_name'>
  }
  className?: string
}

const Favorite: FC<PropsFavorite> = ({ active, category, id, analytic, children, className = '' }) => {
  const dispatch = useAppDispatch()

  const handleClick = (e) => {
    e.stopPropagation()
    e.preventDefault()

    const getAnalyticMethod = () => {
      switch (category) {
        case 'restaurant':
          return active ? EGG.entity.restaurant.click_remove_favorite : EGG.entity.restaurant.click_add_favorite
        case 'selection':
          return active ? EGG.entity.selection.click_remove_favorite : EGG.entity.selection.click_add_favorite
        case 'concert':
          return null
        case 'news':
          return active ? EGG.entity.news.click_remove_favorite : EGG.entity.news.click_add_favorite
        default:
          return null
      }
    }

    const analyticMethod = getAnalyticMethod()

    middlewareAuthorized(() => {
      const actionMethod = active ? deleteFavoriteObject : addFavoriteObject
      const revertActionMethod = active ? addFavoriteObject : deleteFavoriteObject
      dispatch(actionMethod({ object: category, id: id }))

      LightGQL.afisha
        .query(active ? GQL_TAGS.DELETE_FAVORITE : GQL_TAGS.ADD_FAVORITE, {
          variables: {
            input: {
              subjectId: `${CategoryForRequest[category]}_${id}`,
              clientMutationId: 'AfishaRestaurantsForever',
            },
          },
          requestInit: { credentials: 'include' },
          mapping: active ? MAPPING_API_GQL.deleteFavorite : MAPPING_API_GQL.addFavorite,
        })
        .then(() => {
          dispatch(addTemporaryNotice({ markup: active ? 'Удалено из избранного' : 'Добавлено в избранное' }))

          // @ts-ignore
          analyticMethod && analyticMethod(analytic.data, analytic.other)
        })
        .catch(() => {
          dispatch(addTemporaryNotice({ markup: 'Что-то пошло не так, попробуйте позже' }))
          dispatch(revertActionMethod({ object: category, id: id }))
        })
    })
  }

  return (
    <ErrorBoundary>
      <div className={`${s.button} ${className}`} onClick={handleClick} data-active={active}>
        {children}
      </div>
    </ErrorBoundary>
  )
}

export default memo(FavoriteContainer)
